import { useCallback, useEffect, useState } from "react";
import { useRouterQueryOld } from "./useRouterQueryOld.jsx";
import { useRouter } from "next/router";

export const BROWSER_STORAGE_TYPES = {
    LOCAL: "LOCAL",
    SESSION: "SESSION",
    BOTH: "BOTH",
};

export function useBrowserSessionState(props) {
    return useBrowserState({
        ...props,
        browserStorageType: BROWSER_STORAGE_TYPES.SESSION,
    });
}

export function useBrowserLocalState(props) {
    return useBrowserState({
        ...props,
        browserStorageType: BROWSER_STORAGE_TYPES.LOCAL,
    });
}

export function useBrowserSessionAndLocalState(props) {
    return useBrowserState({
        ...props,
        browserStorageType: BROWSER_STORAGE_TYPES.BOTH,
    });
}

export function useBrowserState({
    key,
    defaultValue,
    initWithDefaultValue = false,
    approvedValues,
    approvedFn,
    timeInHours,
    includeQuery = false,
    shouldUpdateQuery = true,
    isCookiesAcceptedFn,
    browserStorageType,
    formatFn,
}) {
    function areCookiesAcceptable() {
        return !isCookiesAcceptedFn || isCookiesAcceptedFn();
    }

    const router = useRouter();

    const { query, updateQuery } = useRouterQueryOld();

    const [value, setStateValueLocally] = useState(initWithDefaultValue ? (formatFn ? formatFn(defaultValue) : defaultValue) || "" : "");
    const [initialized, setInitialized] = useState(false);

    const setValueLocally = useCallback(
        (v) => {
            setStateValueLocally(formatFn ? formatFn(v) : v);
        },
        [setStateValueLocally],
    );

    function getStorages() {
        if (browserStorageType === BROWSER_STORAGE_TYPES.BOTH) {
            return [sessionStorage, localStorage];
        }
        if (browserStorageType === BROWSER_STORAGE_TYPES.SESSION) {
            return [sessionStorage];
        }
        return [localStorage];
    }

    const TIME_TIE_FIGHTER_DELIMITER = " |-| ";

    function valueToValueAndTimeMaybe(value) {
        if (timeInHours) {
            const currentDate = new Date();
            // Add the specified hours to the current date
            const newDate = new Date(currentDate.getTime() + timeInHours * 60 * 60 * 1000);
            return value + TIME_TIE_FIGHTER_DELIMITER + newDate.toISOString();
        }

        return value;
    }

    const setValue = useCallback(
        (value) => {
            const formattedValue = formatFn ? formatFn(value) : value;
            setValueLocally(formattedValue);
            if (areCookiesAcceptable()) {
                getStorages().forEach((s) => s?.setItem(key, valueToValueAndTimeMaybe(formattedValue)));
            }
            includeQuery && shouldUpdateQuery && updateQuery({ [key]: formattedValue });
        },
        [key, router],
    );

    useEffect(() => {
        const queryValue = query[key];
        let storageValue = areCookiesAcceptable() && getStorages().reduce((finalValue, s) => (finalValue ? finalValue : s?.getItem(key)), null);
        if (includeQuery && queryValue) {
            // If we began with a queryValue then it has priority
            storageValue = queryValue;
        }
        let datetimeString = null;
        // We always try to split on time
        if (storageValue) {
            const result = storageValue.split(TIME_TIE_FIGHTER_DELIMITER);
            storageValue = result[0];
            datetimeString = result[1];
        }

        if (datetimeString) {
            const todaysTime = new Date().getTime();
            const storageTime = new Date(datetimeString).getTime();
            if (storageTime < todaysTime) {
                storageValue = undefined;
            }
        }

        if (storageValue === "null") {
            storageValue = null;
        }
        if (storageValue === "true") {
            storageValue = true;
        }
        if (storageValue === "false") {
            storageValue = false;
        }

        function approved() {
            if (!approvedFn && !approvedValues) {
                return true;
            }
            if (approvedFn) {
                return approvedFn(storageValue);
            }
            if (approvedValues) {
                return approvedValues.includes(storageValue);
            }
        }

        let chosenValue;
        if (storageValue && approved()) {
            chosenValue = storageValue;
        } else {
            chosenValue = defaultValue;
        }
        includeQuery && shouldUpdateQuery && updateQuery({ [key]: chosenValue });
        setValueLocally(chosenValue);
        setInitialized(true);
    }, []);

    return [value, setValue, initialized];
}
